import React, { useRef } from "react";
import { useDelayedEffect } from "../Hooks/useDelayedEffect";
import { useHotkey } from "../Hooks/useHotkey/useHotkey";
import { toModifierArray } from "./Common";
/**
 * wraps a hotkey around the child components, if focus is on any of the child components the hotkey will be active, otherwise it will not
 * @param props HotkeyProps
 * @returns a JSX element
 */
export function Hotkey(props) {
    const { onDown } = props;
    const modifiers = toModifierArray(props);
    //We want to delay the actual onDown call until the next render when the triggerBlur prop is turned on
    //This is so that any follow on results from the blur have been carried out (e.g. set state calls)
    //We skip the delay when triggerBlur is *not* turned on, as there aren't going to be any follow on effects
    //before our onDown call that we would care about, and we don't want to wait an extra render before activating the hotkey (this delay is actually somewhat noticeable to the user)
    const triggerOnDown = useDelayedEffect(onDown, props.triggerBlur ?? false);
    const ref = useRef(null);
    useHotkey(triggerOnDown, ref, props.hotkey, props.triggerBlur, ...modifiers);
    return (React.createElement("div", { tabIndex: -1, ref: ref }, props.children));
}
//More info on the solution for making sure state is up to date when hotkeys get called:
//This delayed render was one of a few possible solutions to this problem, others were
// * just not having controls that only update on blur when hotkeys are needed
//      this isn't viable because of performance
// * using a hybrid useState + ref, where ref.current always gets updated at the same time as setState is called,
//  so that the hotkey always gets the latest state
//      this option *would* result in up to date data without the extra render, but the code to do this is more complex
//      and I feel has more potential for unexpected issues popping up. Wrapping the input signature that the setState returned from
//      useState has is also difficult as it's hard (impossible? - someone with better type system fu than me might have better luck);
//      to know whether it's the (old) => new function or a concrete new value when the type of the value itself could also be a function.
//Ultimately I went with this useEffect solution to delay activation until the next render, as it's much simpler and easier
//to understand.
